import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions';
import type { StockInPayload, InventoryStockInItem } from '../types/inventoryStockIn.types';

export const inventoryStockInService = {
  async getStockInItem(id: string, companyId: string): Promise<InventoryStockInItem> {
    const { data, error } = await supabase
      .from('inventory_items')
      .select('*')
      .eq('id', id)
      .eq('company_id', companyId)
      .single();

    if (error) throw error;

    return {
      ...data,
      warehouse_name: data.warehouse_name || 'Kho chnh',
    } as InventoryStockInItem;
  },

  async createStockIn(payload: StockInPayload) {
    await permissionEngine.assert(payload.created_by, 'inventory.transactions.create', { 
      companyId: payload.company_id 
    });

    let warehouseId = payload.warehouse_id;

    // If no warehouse_id provided, find the first available warehouse for the company
    if (!warehouseId || warehouseId === '00000000-0000-0000-0000-000000000000') {
      const { data: wh } = await supabase
        .from('warehouses')
        .select('id')
        .eq('company_id', payload.company_id)
        .limit(1)
        .maybeSingle();
      
      if (wh) {
        warehouseId = wh.id;
      } else {
        throw new Error('Khng tm thy kho hng hp l  thc hin giao dch.');
      }
    }

    // 1. Create a batch record
    const batchCode = `B-${new Date().toISOString().replace(/\D/g, '').slice(2, 14)}`;
    const { data: batch, error: batchError } = await supabase
      .from('inventory_batches')
      .insert({
        company_id: payload.company_id,
        item_id: payload.inventory_item_id,
        warehouse_id: warehouseId,
        batch_code: batchCode,
        received_date: payload.transaction_date,
        quantity_on_hand: payload.quantity,
        quantity_available: payload.quantity,
        quantity_reserved: 0,
        cost_per_unit: payload.unit_cost || 0,
        quality_status: 'good',
        nursery_status: 'ready',
      })
      .select('id')
      .single();
    
    if (batchError) throw batchError;

    // 2. Insert transaction
    const { error: transError } = await supabase
      .from('inventory_transactions')
      .insert({
        item_id: payload.inventory_item_id,
        quantity: payload.quantity,
        unit_cost: payload.unit_cost || 0,
        transaction_date: payload.transaction_date,
        transaction_type: 'stock_in',
        note: payload.note,
        company_id: payload.company_id,
        created_by: payload.created_by,
        warehouse_id: warehouseId,
        batch_id: batch.id,
      });

    if (transError) throw transError;

    // 2. Update current stock
    // Note: In a production app, this should be a DB trigger or a single RPC call
    const { data: item } = await supabase
      .from('inventory_items')
      .select('current_stock')
      .eq('id', payload.inventory_item_id)
      .single();
    
    const newStock = (item?.current_stock || 0) + payload.quantity;

    const { error: updateError } = await supabase
      .from('inventory_items')
      .update({ current_stock: newStock, updated_at: new Date().toISOString() })
      .eq('id', payload.inventory_item_id);

    if (updateError) throw updateError;
  },
};
